import * as signalR from "@aspnet/signalr";
import { List } from "immutable";
import { createAction } from "redux-actions";
import * as AlertActions from "./NotificationActions";
import { Thunk } from "./Thunk";
import * as M from "../models";
import { Api } from "../services/Api";
import Settings from "../settings";

const prefix = "ABSTRACT_";

export class Constants {
    public static EVENT_ABSTRACTS_SUBSCRIBED = prefix + "EVENT_ABSTRACTS_SUBSCRIBED";
    public static EVENT_ABSTRACTS_UPDATED_REMOTELY = prefix + "EVENT_ABSTRACTS_UPDATED_REMOTELY";
    public static LOADING_ABSTRACT = prefix + "LOADING_ABSTRACT";
    public static LOADING_EVENTS = prefix + "LOADING_EVENTS";
    public static LOADING_EVENT_TYPE = prefix + "LOADING_EVENT_TYPE";
    public static LOADING_TYPE = prefix + "LOADING_TYPE";
    public static LOADING_USER_ABSTRACTS = prefix + "LOADING_USER_ABSTRACTS";
    public static MEDIA_PROCESSING = prefix + "MEDIA_PROCESSING";
    public static UPDATED_EVENTS = prefix + "UPDATED_EVENTS";
    public static UPDATED_TYPES = prefix + "UPDATED_TYPES";
    public static UPDATED_USER_ABSTRACTS = prefix + "UPDATED_USER_ABSTRACTS";
    public static USER_ABSTRACTS_SUBSCRIBED = prefix + "USER_ABSTRACTS_SUBSCRIBED";
    public static USER_ABSTRACTS_UPDATED_REMOTELY = prefix + "USER_ABSTRACTS_UPDATED_REMOTELY";
    public static WEBSOCKET_UPDATED = prefix + "WEBSOCKET_UPDATED";
}

interface LoadingAbstractPayload {
    abstractId: number | string;
    loading: boolean;
}

interface LoadingEventTypePayload {
    eventId: number | string;
    loading: boolean;
}

interface MediaProcessingPayload {
    mediaId: number;
    processing: boolean;
}

interface LoadingTypePayload {
    typeId: number | string;
    loading: boolean;
}

interface SubscribedPayload {
    id: number;
    subscribed: boolean;
}

export const initConnection = (): Thunk => async (dispatch, getState) => {
    const tryReconnect = () => {
        setTimeout(async () => {
            await dispatch(initConnection());
        }, 10000);
    };

    // if SSR, don't do anything here
    if (typeof window === "undefined")
        return;

    let connection = getState().abstract.websocket;

    // if there is an existing connection, stop it
    if (connection)
        await connection.stop();

    // return if not connected
    if (!getState().conn.connected)
        return;

    try {
        connection = new signalR.HubConnectionBuilder()
            .withUrl(`${Settings.ServicesUrl}/hubs/abstracts`)
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // connection = new signalR.HubConnection(`${Settings.ServicesUrl}/hubs/subscriptions`);
        connection.start().then((obj) => {
            // connection success
            if (connection) {
                dispatch(websocketUpdated(connection));

                connection.onclose((e) => {
                    console.log("Connection closed", e);
                    tryReconnect();
                });
            }
        }, (connErr) => {
            // error connecting
            console.warn("Error connecting", connErr);
            dispatch(websocketUpdated(undefined));
            tryReconnect();
        });

        connection.onclose((obj) => {
            console.warn("Abstracts websocket connection closed", obj);
            dispatch(websocketUpdated(undefined));
            tryReconnect();
        });
    }
    catch (err) {
        console.warn("Error connecting to websocket server", err);
        dispatch(websocketUpdated(undefined));
        await tryReconnect();
    }
};

export const loadEvents = (): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { withAbstractSettings: true }};
    const result = await api.get(Api.endpoints.event.list, qp);

    if (result.isSuccess && result.data)
        dispatch(updateEvents(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingEvents(false));
};

export const loadType = (typeId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.abstract.type.withUrlParams({ id: typeId }));

    if (result.isSuccess && result.data)
        dispatch(updateType(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingType(typeId, false));
};

export const loadTypes = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.abstract.types.withUrlParams({ eventId }));

    if (result.isSuccess && result.data)
        dispatch(updateTypes(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingEventType(eventId, false));
};

export const loadUserAbstracts = (userId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { userId }};
    const result = await api.get(Api.endpoints.abstract.abstracts, qp);

    if (result.isSuccess && result.data)
        dispatch(updateUserAbstracts(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingUserAbstracts(false));
};

export const removeUserAbstract = (abstract: M.Abstract.Abstract): Thunk => async (dispatch, getState) => {
    const abstracts = getState().abstract.userAbstracts;
    const idx = abstracts.findIndex((x: M.Abstract.Abstract) => x.guid === abstract.guid);
    if (idx > -1)
        dispatch(onUserAbstractsUpdated(abstracts.remove(idx)));
};

export const resetEventAbtractsSubscriptions = (): Thunk => (dispatch, getState) => {
    const eventIds = Object.keys(getState().abstract.subscribedEventAbstracts);

    eventIds.forEach((id) => {
        console.log("unsubscribing from event abstracts", id);
        dispatch(onEventAbstractsSubscribed(parseInt(id, 10), false));
    });

    eventIds.forEach(async (id) => {
        console.log("re-subscribing to event abstracts", id);
        await dispatch(subscribeToEventAbstracts(parseInt(id, 10)));
    });
};

export const resetUserAbtractsSubscriptions = (): Thunk => (dispatch, getState) => {
    const eventIds = Object.keys(getState().abstract.subscribedUserAbstracts);

    eventIds.forEach((id) => {
        console.log("unsubscribing from user abstracts", id);
        dispatch(onUserAbstractsSubscribed(parseInt(id, 10), false));
    });

    eventIds.forEach(async (id) => {
        console.log("re-subscribing to user abstracts", id);
        await dispatch(subscribeToUserAbstracts(parseInt(id, 10)));
    });
};

export const subscribeToEventAbstracts = (eventId: number): Thunk => async (dispatch, getState) => {
    const sub = getState().abstract.subscribedEventAbstracts[eventId];
    if (sub !== undefined)
        return;

    const connection = getState().dbSubscriptions.websocket;
    if (!connection)
        return;

    try {
        connection.on("EventAbstractsUpdated", (id) => {
            if (id === eventId) {
                console.log("event abstracts received updates", id);
                dispatch(onEventAbtractsUpdatedRemotely(id));
            }
        });

        connection.on("EventAbstractsSubscribed", (id) => {
            if (id === eventId) {
                console.log("subscribed to event abstracts updates for event ID", id);
                dispatch(onEventAbstractsSubscribed(id, true));
            }
        });

        // subscribe to changes
        await connection.invoke("EventAbstractsSubscribe", eventId);
    }
    catch (err) {
        console.warn("Subscription error", eventId, err);
    }
};

export const subscribeToMediaProcessing = (mediaId: number): Thunk => async (dispatch, getState) => {
    const connection = getState().dbSubscriptions.websocket;
    if (!connection)
        return;

    try {
        connection.on("MediaProcessingComplete", (mId) => {
            // Media has finished processing
            console.log(`Media processing completed for ID ${mId}.`);
            dispatch(toggleMediaProcessing(parseInt(mId, 10), false));
        });

        connection.on("MediaProcessingSubscribed", (mId) => {
            // We are subscribed to media processing updates.
            console.log("subscribed to media processing updates for ID", mId);
        });

        connection.on("MediaProcessingUnsubscribed", (mId) => {
            console.log("unsubscribed from media processing updates for ID", mId);
        });

        // subscribe to nav changes for this app
        // await connection.invoke("PageUnsubscribe", pageId);
        await connection.invoke("MediaProcessingSubscribe", mediaId);
    }
    catch (err) {
        console.warn("Subscription error", mediaId, err);
    }
};

export const subscribeToUserAbstracts = (userId: number): Thunk => async (dispatch, getState) => {
    const sub = getState().abstract.subscribedUserAbstracts[userId];
    if (sub !== undefined)
        return;

    const connection = getState().dbSubscriptions.websocket;
    if (!connection)
        return;

    try {
        connection.on("UserAbstractsUpdated", (id) => {
            // Updates received
            if (id === userId) {
                console.log(`received updates. Will refetch user abstracts (${id}) shortly...`);
                dispatch(onUserAbtractsUpdatedRemotely(id));
            }
        });

        connection.on("UserAbstractsSubscribed", (id) => {
            if (id === userId) {
                console.log("subscribed to user abstract updates for user", id);
                dispatch(onUserAbstractsSubscribed(id, true));
            }
        });

        // subscribe to changes
        await connection.invoke("UserAbstractsSubscribe", userId);
    }
    catch (err) {
        console.warn("Subscription error", userId, err);
    }
};

export const updateEvent = (evt: M.Event.Event): Thunk => async (dispatch, getState) => {
    dispatch(updateEvents([evt]));
};

export const updateEvents = (updatedEvents: M.Event.Event[] | List<M.Event.Event>): Thunk => async (dispatch, getState) => {
    let events = getState().abstract.events;
    const _updatedEvents: M.Event.Event[] = !Array.isArray(updatedEvents)
        ? updatedEvents.toArray()
        : updatedEvents;

    _updatedEvents.map((e: M.Event.Event) => {
        const idx = events.findIndex((x: M.Event.Event) => x.id === e.id);
        events = idx > -1
            ? events.set(idx, e)
            : events.push(e);
    });

    dispatch(onEventsUpdated(events));
};

export const updateType = (type: M.Abstract.AbstractType): Thunk => async (dispatch, getState) => {
    dispatch(updateTypes([type]));
};

export const updateTypes = (updatedTypes: M.Abstract.AbstractType[] | List<M.Abstract.AbstractType>): Thunk => async (dispatch, getState) => {
    let types = getState().abstract.types;
    const _updatedTypes: M.Abstract.AbstractType[] = !Array.isArray(updatedTypes)
        ? updatedTypes.toArray()
        : updatedTypes;

    _updatedTypes.map((type: M.Abstract.AbstractType) => {
        const idx = types.findIndex((x: M.Abstract.AbstractType) => x.id === type.id);
        types = idx > -1
            ? types.set(idx, type)
            : types.push(type);
    });

    dispatch(onTypesUpdated(types));
};

export const updateUserAbstract = ($abstract: M.Abstract.Abstract): Thunk => async (dispatch, getState) => {
    dispatch(updateUserAbstracts([$abstract]));
};

export const updateUserAbstracts = (updatedAbstracts: M.Abstract.Abstract[] | List<M.Abstract.Abstract>): Thunk => async (dispatch, getState) => {
    let abstracts = getState().abstract.userAbstracts;
    const _updatedAbstracts: M.Abstract.Abstract[] = !Array.isArray(updatedAbstracts)
        ? updatedAbstracts.toArray()
        : updatedAbstracts;

    _updatedAbstracts.map((a: M.Abstract.Abstract) => {
        const idx = abstracts.findIndex((x: M.Abstract.Abstract) => x.id === a.id);
        abstracts = idx > -1
            ? abstracts.set(idx, a)
            : abstracts.push(a);
    });

    dispatch(onUserAbstractsUpdated(abstracts));
};

export const onEventAbstractsSubscribed = createAction<SubscribedPayload, number, boolean>(
    Constants.EVENT_ABSTRACTS_SUBSCRIBED, (id, subscribed) => ({ id, subscribed }),
);

export const onEventAbtractsUpdatedRemotely = createAction<number, number>(
    Constants.EVENT_ABSTRACTS_UPDATED_REMOTELY, (eventId) => (eventId),
);

export const onEventsUpdated = createAction<List<M.Event.Event>, List<M.Event.Event>>(
    Constants.UPDATED_EVENTS, (events) => (events),
);

export const onTypesUpdated = createAction<List<M.Abstract.AbstractType>, List<M.Abstract.AbstractType>>(
    Constants.UPDATED_TYPES, (types) => (types),
);

export const onUserAbstractsSubscribed = createAction<SubscribedPayload, number, boolean>(
    Constants.USER_ABSTRACTS_SUBSCRIBED, (id, subscribed) => ({ id, subscribed }),
);

export const onUserAbstractsUpdated = createAction<List<M.Abstract.Abstract>, List<M.Abstract.Abstract>>(
    Constants.UPDATED_USER_ABSTRACTS, (abstracts) => (abstracts),
);

export const onUserAbtractsUpdatedRemotely = createAction<number, number>(
    Constants.USER_ABSTRACTS_UPDATED_REMOTELY, (userId) => (userId),
);

export const toggleLoadingAbstract = createAction<LoadingAbstractPayload, number | string, boolean>(
    Constants.LOADING_ABSTRACT, (abstractId, loading) => ({ abstractId, loading }),
);

export const toggleLoadingEvents = createAction<boolean, boolean>(
    Constants.LOADING_EVENTS, (loading) => (loading),
);

export const toggleLoadingEventType = createAction<LoadingEventTypePayload, number | string, boolean>(
    Constants.LOADING_EVENT_TYPE, (eventId, loading) => ({ eventId, loading }),
);

export const toggleLoadingUserAbstracts = createAction<boolean, boolean>(
    Constants.LOADING_USER_ABSTRACTS, (loading) => (loading),
);

export const toggleMediaProcessing = createAction<MediaProcessingPayload, number, boolean>(
    Constants.MEDIA_PROCESSING, (mediaId, processing) => ({ mediaId, processing }),
);

export const toggleLoadingType = createAction<LoadingTypePayload, number | string, boolean>(
    Constants.LOADING_TYPE, (typeId, loading) => ({ typeId, loading }),
);

export const websocketUpdated = createAction<signalR.HubConnection | undefined, signalR.HubConnection | undefined>(
    Constants.WEBSOCKET_UPDATED, (connection) => (connection),
);



// WEBPACK FOOTER //
// ./src/actions/AbstractActions.ts